import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveView } from '@/stores/viewMode.ts';
import { useUi } from '@/stores/ui.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { ChannelGrid } from '@/features/channels/ChannelGrid.tsx';
import { GuidePage } from '@/features/guide/GuidePage.tsx';

/**
 * Live TV — one destination, two presentations.
 *
 * The guide and the tile grid were separate navigation items, which put a
 * view mode in the navigation bar: a control beside the screen that belongs
 * inside it, costing a permanent focus stop for a choice made once. They are
 * the same channels either way.
 *
 * Which one you get is remembered per device (see stores/viewMode.ts) and
 * changed from the MENU key, where the other things you do to this screen
 * already live.
 *
 * No shared wrapper chrome on purpose: the guide owns the whole viewport and
 * the grid scrolls inside the normal layout, so anything drawn here would
 * have to be right for both and would end up right for neither.
 */
export function LiveTvPage() {
  const view = useLiveView((s) => s.view);
  const navigate = useNavigate();

  /*
   * BACK LEAVES LIVE TV, and without this there was no reliable way out.
   *
   * The guide paints its own cursor and has no focusable elements at all, so
   * spatial navigation cannot walk out of it — it hands UP back to the shell
   * only from the very top row, which on a hundred-and-twelve channel lineup
   * is a long way from wherever you are. Nothing claimed BACK either, so the
   * press fell through to the Android wrapper and walked web history, or left
   * the app. Every other screen in this app returns to the library on BACK;
   * this was the one that did not.
   *
   * Only while BROWSING. In theater or fullscreen, BACK means "one
   * presentation tier down" — see usePlayerKeys — and stealing it here would
   * make a full-screen channel impossible to shrink.
   */
  useEffect(() => pushKeyLayer((k: NavKey) => {
    if (k !== 'back') return false;
    if (useUi.getState().view !== 'browse') return false;
    navigate('/library');
    return true;
  }), [navigate]);

  return view === 'guide' ? <GuidePage /> : <ChannelGrid kind="tv" />;
}
